import React, { useRef, useState } from "react";
import { Box, Button, Stack, Typography, alpha } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

/**
 * Zona de selección de archivos con borde punteado.
 * Al seleccionar archivos, extrae SOLO metadata (name, size, type) — NO lee contenido.
 *
 * @param {{ onUpload: (files: Array<{fileName,fileSize,fileType}>) => void }} props
 */
export default function DocumentUploadArea({ onUpload }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;

    const metadataArr = Array.from(fileList).map((f) => ({
      fileName: f.name,
      fileSize: f.size,
      fileType: f.type || "application/octet-stream",
    }));

    onUpload(metadataArr);

    // Reset input para poder re-seleccionar el mismo archivo
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleInputChange = (e) => handleFiles(e.target.files);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      sx={{
        border: "2px dashed",
        borderColor: dragOver ? "primary.main" : "divider",
        borderRadius: 3,
        p: { xs: 3, md: 4 },
        textAlign: "center",
        cursor: "pointer",
        transition: "all 0.2s ease",
        bgcolor: dragOver
          ? (theme) => alpha(theme.palette.primary.main, 0.04)
          : "transparent",
        "&:hover": {
          borderColor: "primary.light",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
        },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={handleInputChange}
      />

      <Stack spacing={1.5} alignItems="center">
        <CloudUploadIcon
          sx={{ fontSize: 48, color: dragOver ? "primary.main" : "text.disabled" }}
        />
        <Typography variant="body1" fontWeight={600} color="text.secondary">
          Drag & drop files here or click to browse
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Only file metadata (name, size, type) is stored — file content is not uploaded.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CloudUploadIcon />}
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
          sx={{ textTransform: "none", mt: 1 }}
        >
          Select Files
        </Button>
      </Stack>
    </Box>
  );
}
